import React, { Fragment, useEffect, useState, useRef } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ReactSVG } from "react-svg";
import { useDispatch } from "react-redux";
import CustomInput from "../../../../common/CustomInput";
import InterventionCreate from "./interventionCreate";
import InterventionAssessment from "./interventionAssessment";
import SafetyNote from "../../../../common/tooltip/medication-review/safety-note";
import NewToolTip from "../../../../common/New-ToolTip";
import EffectivenessNote from "../../../../common/tooltip/medication-review/effectiveness-note";
import MedicalConditionSelect from "../../services/select/medical-condition";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import {
  reasonOptions,
  specialInstructionOptions,
} from "../clinical-assessment/data";
import {
  CommonQuestionValueTypes,
  AdherenceTypes,
  GeneralTimeTypes,
  getGeneralTimeFrequency,
  DEFAULT_MEDICATION_TYPE_THERAPY,
  medicationTypeTherapyOptions,
  normalizeMedicationTypeTherapy,
} from "./data";
import Alert from "../../../../common/Alert";
import Button from "../../../../common/Button";
import CustomTimeDialog from "./CustomTimeDialog";
import axios from "axios";

/** Value in `specialInstructionOptions` for “Add Custom Instructions”. */
const SPECIAL_INSTRUCTION_OTHER_VALUE = "Other";

const SPECIAL_INSTRUCTIONS_MULTI_SEP = " ||| ";
const SPECIAL_INSTRUCTION_VALUE_SET = new Set(
  specialInstructionOptions.map((o) => o.value)
);

/** Parse stored `specialIntructions`: multi (` ||| `), legacy single, or Other; custom. */
function parseSpecialInstructionsMulti(stored) {
  const empty = { presetValues: [], customText: "" };
  if (stored == null || String(stored).trim() === "") return empty;
  const s = String(stored).trim();
  const O = SPECIAL_INSTRUCTION_OTHER_VALUE;
  const prefix = `${O}; `;

  if (s.includes(SPECIAL_INSTRUCTIONS_MULTI_SEP)) {
    const presetValues = [];
    const add = (v) => {
      if (!presetValues.includes(v)) presetValues.push(v);
    };
    let customText = "";
    for (const part of s.split(SPECIAL_INSTRUCTIONS_MULTI_SEP).map((p) => p.trim()).filter(Boolean)) {
      if (part === O) add(O);
      else if (part.startsWith(prefix)) {
        add(O);
        customText = part.slice(prefix.length);
      } else if (SPECIAL_INSTRUCTION_VALUE_SET.has(part)) add(part);
      else {
        add(O);
        customText = customText ? `${customText} ${part}` : part;
      }
    }
    return { presetValues, customText };
  }

  if (s === O) return { presetValues: [O], customText: "" };
  if (s.startsWith(prefix)) return { presetValues: [O], customText: s.slice(prefix.length) };
  if (SPECIAL_INSTRUCTION_VALUE_SET.has(s)) return { presetValues: [s], customText: "" };
  return s ? { presetValues: [O], customText: s } : empty;
}

function buildSpecialInstructionsMulti(presetValues, customText) {
  const cleaned = (presetValues || []).filter((v) => v != null && String(v).trim());
  if (!cleaned.length) return null;
  const O = SPECIAL_INSTRUCTION_OTHER_VALUE;
  const t = (customText || "").trim();
  const segments = cleaned.map((v) => (v === O ? (t ? `${O}; ${t}` : O) : v));
  return segments.length === 1 ? segments[0] : segments.join(SPECIAL_INSTRUCTIONS_MULTI_SEP);
}

const MedicationReviewCard = ({
  medication,
  onFocus,
  patientId,
  medications,
  saveCard = () => {},
  deleteCard = () => {},
  skipCard = () => {},
  revertSkipCard = () => {},
  moveToCard = () => {},
  updateUnsavedCard = () => {},
  onInterventionDialogOpenChange = () => {},
  showCompliance = false,
  monographStatusByIdentifier = {},
}) => {
  const dispatch = useDispatch();
  const [medicationCardObj, setMedicationCardObj] = useState(null);
  const [conditionNameInputMode, setConditionNameInputMode] = useState(false);
  const [openUncodedDrug, setOpenUncodedDrug] = useState(false);

  const [showAssessmentDialog, setshowAssessmentDialog] = useState(false);
  const [showCreateDialog, setshowCreateDialog] = useState(false);
  const [initialRevisitedCardsValue, setInitialRevisitedCardsValue] = useState(
    []
  );
  const [updatedCards, setUpdatedCards] = useState([]);
  const [showPrescribedDialog, setShowPrescribedDialog] = useState(false);
  const [prescribedDialogTitle, setPrescribedDialogTitle] = useState(null);
  const [prescribedDialogMessage, setPrescribedDialogMessage] = useState(null);
  const [isGeneralTime, setIsGeneralTime] = useState(true);
  const [complianceAction, setComplianceAction] = useState("Edit");
  const [openCustomTimePopup, setOpenCustomTimePopup] = useState(false);
  const [isBodyScrollLocked, setIsBodyScrollLocked] = useState(false);
  const [openGeneralInterventionPopup, setOpenGeneralInterventionPopup] = useState(false);
  const customizeButtonRef = useRef(null);
  const assessmentCommentTextareaRef = useRef(null);
  useEffect(() => {
    if (medicationCardObj?.status !== "blank") {
      checkCardUpdates();
    } else {
      setComplianceAction("Save");
      setInitialRevisitedCardsValue([
        ...initialRevisitedCardsValue.filter((x) => x.id !== medication.id),
        medication,
      ]);
    }
    //if confirm (save) btn is disabled then the card is saved
    updateUnsavedCard(
      medicationCardObj?.status === "blank" &&
        medicationCardObj?.name !== null &&
        (initialRevisitedCardsValue.some(
          (x) => x.id === medicationCardObj?.id
        ) ||
          medicationCardObj?.status !== "reviewed")
    );
  }, [medicationCardObj]);

  useEffect(() => {
    setMedicationCardObj(
      medication
        ? {
            ...medication,
            medicationType: normalizeMedicationTypeTherapy(medication.medicationType),
          }
        : null
    );
  }, [medication]);

  // Tell the parent whenever an intervention dialog is open for this card so it
  // can defer rebuilding medCards (which would unmount this card + its portal
  // and drop the not-yet-persisted card being worked on).
  useEffect(() => {
    const isOpen = showAssessmentDialog || showCreateDialog;
    onInterventionDialogOpenChange(isOpen);
    return () => {
      if (isOpen) onInterventionDialogOpenChange(false);
    };
  }, [showAssessmentDialog, showCreateDialog]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const update = () => {
      const body = document.body;
      const overflow =
        body?.style.overflow || (body && window.getComputedStyle(body).overflow);
      setIsBodyScrollLocked(overflow === "hidden");
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => observer.disconnect();
  }, []);

  /** Grow height with content; cap at max-h-[7.5rem], then scroll inside (native textarea does not auto-expand). */
  useEffect(() => {
    const el = assessmentCommentTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeightPx = parseFloat(getComputedStyle(el).lineHeight) || 24;
    const maxHeightPx = parseFloat(getComputedStyle(el).maxHeight);
    const maxPx =
      Number.isFinite(maxHeightPx) && maxHeightPx > 0
        ? maxHeightPx
        : lineHeightPx * 5;
    const next = Math.min(el.scrollHeight, maxPx);
    const minPx = 50;
    el.style.height = `${Math.max(next, minPx)}px`;
  }, [medicationCardObj?.comment, medicationCardObj?.id]);

  const onSaveAssessment = (assess, createIntervention) => {
    let currentIntervention = medicationCardObj.intervention;
    let currentInterventionId = medicationCardObj.interventionId;
    currentIntervention = Object.assign({}, currentIntervention, assess);

    medicationCardObj.intervention = currentIntervention;
    medicationCardObj.interventionId = currentInterventionId;

    if (!createIntervention) {
      onContinue(medicationCardObj);
    }
  };

  const onSaveInterventionAndAssessment = (interventionObj, interventionId, goBack) => {
    let currentIntervention = medicationCardObj.intervention;
    currentIntervention = Object.assign(
      {},
      currentIntervention,
      interventionObj
    );

    medicationCardObj.interventionId = interventionId;
    medicationCardObj.intervention = currentIntervention;

    if (!goBack) {
      onContinue(medicationCardObj, true);
    }
  };

  const handleSelection = (id, field, val) => {
    // Check if the object was found
    if (id !== -1) {
      let updatedMed = null;
      if (field === "reasonForMedication")
        updatedMed = {
          ...medicationCardObj,
          reasonForMedication: val?.label || null,
          reasonForMedicationCode: val?.value || null,
        };
      // Update the specified field with the new value
      else updatedMed = { ...medicationCardObj, [field]: val };

      if (field === "consumption")
        updatedMed = { ...updatedMed, intervention: null, interventionId: null };

      setMedicationCardObj(updatedMed);
    }
  };

  const onSaveAndContinue = (med) => {
    if (med.consumption === AdherenceTypes.AsPrescribed) {
      managePrescribedDialog(med);
    } else {
      handleIntervention(med.consumption);
    }
  };

  const onContinue = (med, isIntervention = false) => {
    if (med.intervention && !isIntervention) {
      medicationCardObj.intervention.urgencyLevel = null;
      medicationCardObj.intervention.pharmacistComments = null;
      medicationCardObj.intervention.actionPlan = null;
      medicationCardObj.intervention.interventionType = null;
      medicationCardObj.intervention.drugTherapyProblem = null;
      medicationCardObj.interventionId = null;
    }

    let reviewedMed = { ...med, status: "reviewed" };
    setInitialRevisitedCardsValue(
      initialRevisitedCardsValue.filter((x) => x.id !== med.id)
    );
    setUpdatedCards(updatedCards.filter((x) => x !== med.id));
    saveCard(reviewedMed);
  };

  useEffect(() => {
    dispatch({
      type: "SET_AMR_UPDATED_REVIEWCARDS_LOCAL",
      payload: updatedCards,
    });
  }, [updatedCards]);

  useEffect(() => {
    dispatch({
      type: "SET_AMR_INITIAL_VALUE_REVIEWCARDS_LOCAL",
      payload: initialRevisitedCardsValue,
    });
  }, [initialRevisitedCardsValue]);

  const onSkipReview = (med) => {
    let updatedMed = {
      ...med,
      status: "skipped",
      consumption: null,
      safe: null,
      technique: null,
      working: null,
      intervention: null,
      interventionId: null
    };

    setInitialRevisitedCardsValue(
      initialRevisitedCardsValue.filter((x) => x.id !== med.id)
    );
    setUpdatedCards(updatedCards.filter((x) => x !== med.id));
    skipCard(updatedMed);
  };

  const onRevertSkip = (med) => {
    let updatedMed = {
      ...med,
      status: "blank",
      medicationType: DEFAULT_MEDICATION_TYPE_THERAPY,
    };

    revertSkipCard(updatedMed);
  };

  const managePrescribedDialog = (med) => {
    if (
      med.safe === CommonQuestionValueTypes.Yes &&
      med.working === CommonQuestionValueTypes.No
    ) {
      setPrescribedDialogTitle("Medication Effectiveness and Safety");
      setPrescribedDialogMessage(
        "Medication may not be effective, potential side effects may impact adherence. May reassess the treatment plan and consider to create an intervention."
      );
      setShowPrescribedDialog(true);
    } else if (med.safe === CommonQuestionValueTypes.Yes) {
      setPrescribedDialogTitle("Medication Safety");
      setPrescribedDialogMessage(
        "Possible side effects may affect medication adherence. May reassess the treatment plan and consider to create an intervention."
      );
      setShowPrescribedDialog(true);
    } else if (med.working === CommonQuestionValueTypes.No) {
      setPrescribedDialogTitle("Medication Effectiveness");
      setPrescribedDialogMessage(
        "Medication may not be effective. May reassess the treatment plan and consider to create an intervention."
      );
      setShowPrescribedDialog(true);
    } else {
      onContinue(med);
    }
  };

  const checkCardUpdates = () => {
    let initialCardValue = initialRevisitedCardsValue.find(
      (x) => x?.id === medicationCardObj?.id
    );
    if (
      medicationCardObj !== undefined &&
      medicationCardObj !== null &&
      initialCardValue !== undefined &&
      initialCardValue !== null
    ) {
      if (medicationCardObj?.generalTime === initialCardValue?.generalTime)
        setComplianceAction("Edit");
      else setComplianceAction("Update");

      if (
        medicationCardObj?.consumption === initialCardValue?.consumption &&
        medicationCardObj?.safe === initialCardValue?.safe &&
        medicationCardObj?.medicationType ===
          initialCardValue?.medicationType &&
        medicationCardObj?.working === initialCardValue?.working &&
        medicationCardObj?.technique === initialCardValue?.technique &&
        medicationCardObj?.comment === initialCardValue?.comment &&
        medicationCardObj?.specialIntructions ===
          initialCardValue?.specialIntructions &&
        medicationCardObj?.reasonForMedication ===
          initialCardValue?.reasonForMedication &&
        medicationCardObj?.status === initialCardValue?.status &&
        medicationCardObj?.generalTime === initialCardValue?.generalTime
      ) {
        // inform parent
        setUpdatedCards(updatedCards.filter((x) => x !== medicationCardObj.id));
        return true;
      } else {
        // inform parent
        setUpdatedCards([...updatedCards, medicationCardObj.id]);
        return false;
      }
    }
  };

  const handleIntervention = (consumption) => {
    if (consumption !== AdherenceTypes.AsPrescribed) {
      setshowAssessmentDialog(true);
    } else {
      setshowCreateDialog(true);
    }
  };

  const deleteCurrentCard = () => {
    setInitialRevisitedCardsValue(
      initialRevisitedCardsValue.filter((x) => x.id !== medicationCardObj.id)
    );
    setUpdatedCards(updatedCards.filter((x) => x !== medicationCardObj.id));
    deleteCard();
  };  

  const RxVigilanceDinSearch = ({ din }) => {
    const vigilanceEnv = import.meta.env.VITE_VIGILANCE_ENV || "int";
    const rxVigilanceBaseUrl = `https://rx.${vigilanceEnv}.vigilance.ca`;
    const rxVigilanceAuthUrl = `https://rxauth.${vigilanceEnv}.vigilance.ca`;

    const monographStatus =
      din != null ? monographStatusByIdentifier[String(din)] : null;
    const hasMonograph = monographStatus?.hasMonograph;
    const monographId = monographStatus?.monographId;

    const handleClick = async () => {
      if (!hasMonograph || !monographId) {
        return;
      }

      try {
        const tokenResponse = await axios.post(
          `${rxVigilanceAuthUrl}/token`,
          new URLSearchParams({
            client_id: import.meta.env.VITE_APP_VIGILANCE_ID,
            client_secret: import.meta.env.VITE_APP_VIGILANCE_SECRET,
            user_id: "REEL01",
            grant_type: "client_credentials",
          }),
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );

        const token = tokenResponse.data.access_token;
        openMonographForm(token, monographId);
      } catch (error) {
        console.error("Failed to open monograph:", error);
      }
    };

    const openMonographForm = (token, monographId) => {
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${rxVigilanceBaseUrl}/module/mono/en/${monographId}.htm`;
      form.target = "_blank";

      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "token";
      tokenInput.value = token;

      form.appendChild(tokenInput);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    };

    const tooltipId = `monograph-${din}`;

    return (
      <>
        <img
          src="/icons/rx-vigilance-icon.svg"
          alt="DIN Monograph"
          data-tooltip-id={tooltipId}
          className="cursor-pointer w-10 min-w-[40px]"
          onClick={handleClick}
        />
        {monographStatus && !hasMonograph && (
          <NewToolTip
            id={tooltipId}
            place="bottom"
            render={() => (
              <div className="flex flex-col max-w-md p-2 bg-gray-20">
                <p className="text-sm text-teal-600">No monograph available</p>
              </div>
            )}
          />
        )}
      </>
    );
  };

  const canShowCardActions =
    medicationCardObj?.status !== "blank" &&
    !initialRevisitedCardsValue.some((x) => x?.id === medicationCardObj?.id);

  const specialInstructionsParsed = medicationCardObj
    ? parseSpecialInstructionsMulti(medicationCardObj.specialIntructions)
    : { presetValues: [], customText: "" };
  const siDisabled =
    !medicationCardObj ||
    !initialRevisitedCardsValue.some((x) => x.id === medicationCardObj.id);

  return (
    <div className="relative">
      <div
        className={`relative flex flex-row bg-white rounded-[4px] shadow-lg shadow-gray-400 overflow-visible ${
          onFocus && "border-2 border-REELTeal"
        }`}
      >
        {/* Bottom border - hidden when dialogs or slider are open */}
        {!(showAssessmentDialog || showCreateDialog || openUncodedDrug || openCustomTimePopup || isBodyScrollLocked) && (
          <div
            className={`pointer-events-none absolute bottom-0 left-0 right-0 z-0 ${onFocus ? "h-[5px] bg-REELTeal" : "h-[3px] bg-gray-100"}`}
          />
        )}
      {medicationCardObj && (
        <div className="flex w-full h-[598px]">
          {/* Review Card */}
          <div className="border-r border-gray-100 flex flex-1 min-w-0 min-h-0 flex-col">
            {/* header */}
            <div
              className={`h-[197px] px-8 pt-6 pb-3 gap-5 ${
                medicationCardObj.name !== null ? "bg-gray-20 relative z-0" : "bg-blue-100 relative z-0"
              }`}
            >
              <div className="flex flex-row justify-between">
                <h3 className="text-2xl font-semibold text-teal-300 max-w-[760px] min-h-[74px]">
                  {medicationCardObj.name === null
                    ? "Select a medication from Pre-Review"
                    : medicationCardObj.cardName} {" "}
                  {isGeneralTime && getGeneralTimeFrequency(medicationCardObj)}

                  <span className="block text-xl text-gray-800 font-normal">
                    {medicationCardObj?.manufacturerStoredType === "Small" &&
                    medicationCardObj?.manufacturerCode
                      ? `(${medicationCardObj.manufacturerCode})`
                      : medicationCardObj?.manufacturerStoredType === "Full" &&
                        medicationCardObj?.manufacturerName
                      ? `(${medicationCardObj.manufacturerName})`
                      : ""}
                  </span>
                </h3>
                <div className="flex flex-row">
                  <div className="flex pl-2 pr-8 items-start">
                    <RxVigilanceDinSearch
                      din={
                        medications?.find(
                          (med) => med?.id === medicationCardObj?.id
                        )?.medicationGenCode
                      }
                    />
                  </div>
                  {canShowCardActions && (
                    <Menu
                      as="div"
                      className="relative flex pt-3 pr-2 justify-end"
                    >
                      <div>
                        <Menu.Button>
                          <ReactSVG
                            src={`/icons/triple-dot.svg`}
                            className={`flex w-2 scale-x-[1.6] scale-y-[1.6]`}
                          />
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform translate-y-2 opacity-0 scale-95"
                        enterTo="transform translate-y-0 opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform translate-y-0 opacity-100 scale-100"
                        leaveTo="transform translate-y-2 opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute left-6 top-0 w-24 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-opacity-5">
                          <Menu.Item>
                            <button
                              className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100"
                              onClick={(e) => {
                                if (medicationCardObj.status === "skipped")
                                  onRevertSkip(medicationCardObj);
                                setInitialRevisitedCardsValue([
                                  ...initialRevisitedCardsValue.filter(
                                    (x) => x.id !== medicationCardObj.id
                                  ),
                                  medicationCardObj,
                                ]);
                                moveToCard();
                              }}
                            >
                              Edit
                            </button>
                          </Menu.Item>
                          <Menu.Item>
                            <button
                              className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100"
                              onClick={() => {
                                deleteCurrentCard();
                              }}
                            >
                              Delete
                            </button>
                          </Menu.Item>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  )}
                </div>
              </div>
              <div
                className={`flex flex-row space-x-5 h-[92px] ${
                  medicationCardObj.name !== null ||
                  (medicationCardObj.name !== undefined &&
                    "pointer-events-none")
                } ${!onFocus && "pointer-events-none"}`}
              >
                <div className="flex flex-col w-full">
                  {
                    <MedicalConditionSelect
                      onNewCreate={(value) => setConditionNameInputMode(value)}
                      value={{
                        label: medicationCardObj.reasonForMedication,
                        name: medicationCardObj.reasonForMedication,
                        value: medicationCardObj.reasonForMedicationCode,
                        id: medicationCardObj.reasonForMedicationCode,
                      }}
                      onChange={(condition) => {
                        handleSelection(
                          medicationCardObj.id,
                          "reasonForMedication",
                          condition
                        );
                      }}
                      defaultOptions={reasonOptions}
                      error={
                        medicationCardObj.name !== null &&
                        !medicationCardObj.reasonForMedication
                      }
                      class2={"bg-white !text-base"}
                      label="Reason"
                      tyoe="reason"
                      isCreatable={true}
                      isRequired={true}
                      disabled={
                        !initialRevisitedCardsValue.some(
                          (x) => x?.id === medicationCardObj.id
                        ) && medicationCardObj.status !== "blank"
                      }
                      useMenuPortal={true}
                    />
                  }
                  {conditionNameInputMode === true && (
                    <>
                      <p className="col-span-12 text-body-sm text-red-200 font-medium">
                        This is an UNCODED MEDICAL CONDITION- No Clinical Check
                        will be performed.
                      </p>
                    </>
                  )}
                </div>
                <div className="flex flex-col w-full">
                  {
                    <CustomInput
                      options={medicationTypeTherapyOptions}
                      name={`medicationType`}
                      className="col-span-12 w-full !bg-white"
                      inputClassName="!min-h-[30px]"
                      label="Medication type | Therapy"
                      type="dropdown"
                      value={medicationCardObj.medicationType}
                      onChange={(e) => {
                        handleSelection(
                          medicationCardObj.id,
                          "medicationType",
                          e.value
                        );
                      }}
                      required={true}
                      disabled={
                        !initialRevisitedCardsValue.some(
                          (x) => x.id === medicationCardObj.id
                        ) && medicationCardObj.status !== "blank"
                      }
                    />
                  }
                </div>
                {!showCompliance && (
                  <button
                    ref={customizeButtonRef}
                    className={`font-semibold text-body-md p-3 rounded-[4px] border-[1px] max-h-[74px] min-w-[200px] bg-white border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-gray-50 ${
                      !initialRevisitedCardsValue.some(
                        (x) => x.id === medicationCardObj.id
                      ) && "pointer-events-none"
                    }`}
                    onClick={() => {
                      setOpenCustomTimePopup(true);
                    }}
                  >
                    <p className="font-semibold text-body-md flex justify-center items-center">
                      Customize Time
                    </p>
                  </button>
                )}
              </div>
            </div>
            <div className="border-t border-gray-100 bg-gray-200"></div>
            {/* body — middle scrolls; footer (actions) stays pinned */}
            <div
              className={`flex min-h-0 flex-1 flex-col overflow-hidden text-gray-800
                            ${
                              medicationCardObj.name !== null ||
                              (medicationCardObj.name !== undefined &&
                                "opacity-40 pointer-events-none")
                            } ${!onFocus && "pointer-events-none"}`}
            >
              <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
                  {/* Mid Section */}
                  <div
                    className={`${
                      !initialRevisitedCardsValue.some(
                        (x) => x.id === medicationCardObj.id
                      ) && "pointer-events-none"
                    }`}
                  >
                    <div className="py-2 px-6 flex flex-row space-x-5 h-[68px] items-center">
                      <p className="min-w-[320px] text-gray-800">
                        <span className="text-lg font-semibold text-teal-300 flex flex-row">
                          How are you taking the medication?
                        </span>
                        <span className="flex flex-row text-base font-normal text-gray-800">
                          Assess for Adherence
                        </span>
                      </p>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[188px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.consumption ===
                                    AdherenceTypes.AsPrescribed
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "consumption",
                            AdherenceTypes.AsPrescribed
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          As Prescribed
                        </p>
                      </button>

                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[191px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.consumption ===
                                    AdherenceTypes.NotAsPrescribed
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "consumption",
                            AdherenceTypes.NotAsPrescribed
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Not as Prescribed
                        </p>
                      </button>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[188px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.consumption ===
                                    AdherenceTypes.Stopped
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "consumption",
                            AdherenceTypes.Stopped
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Stopped
                        </p>
                      </button>
                    </div>
                    <div className="py-2 px-6 flex flex-row space-x-5 h-[68px] bg-gray-20 items-center">
                      <p className="min-w-[320px] text-gray-800 col-span-4 pr-7">
                        <span className="flex flex-row items-center text-lg font-semibold text-teal-300">
                          Is the medication working?
                          <span className="flex">
                            <ReactSVG
                              src="/icons/information-dark.svg"
                              className="w-5 ml-3"
                              data-tooltip-id="effectiveness-note"
                            />
                            <NewToolTip
                              id="effectiveness-note"
                              render={() => <EffectivenessNote />}
                            />
                          </span>
                        </span>
                        <span className="flex flex-row text-base font-normal text-gray-800">
                          Assess for Effectiveness{" "}
                        </span>
                      </p>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[188px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.working ===
                                    CommonQuestionValueTypes.Yes
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "working",
                            CommonQuestionValueTypes.Yes
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Yes
                        </p>
                      </button>

                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[191px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.working ===
                                    CommonQuestionValueTypes.No
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "working",
                            CommonQuestionValueTypes.No
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          No
                        </p>
                      </button>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[188px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.working ===
                                    CommonQuestionValueTypes.NotSure
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "working",
                            CommonQuestionValueTypes.NotSure
                          );
                          // setOpenGeneralInterventionPopup(true);
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Not Sure
                        </p>
                      </button>
                    </div>
                    <div className="py-2 px-6 flex flex-row space-x-5 h-[68px] items-center">
                      <p className="min-w-[320px] flex flex-col text-gray-800 col-span-4 pr-7">
                        <span className="flex flex-row text-lg font-semibold text-teal-300">
                          Do you have any side effects
                          <span className="flex items-center">
                            <ReactSVG
                              src="/icons/information-dark.svg"
                              className="w-5 ml-3"
                              data-tooltip-id="safe-note"
                            />
                            <NewToolTip
                              id="safe-note"
                              render={() => <SafetyNote />}
                            />
                          </span>
                        </span>
                        <span className="flex flex-row text-base font-normal text-gray-800">
                          Assess for Safety{" "}
                        </span>
                      </p>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[188px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.safe ===
                                    CommonQuestionValueTypes.Yes
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "safe",
                            CommonQuestionValueTypes.Yes
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Yes
                        </p>
                      </button>

                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[191px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.safe ===
                                    CommonQuestionValueTypes.No
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "safe",
                            CommonQuestionValueTypes.No
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          No
                        </p>
                      </button>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[188px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.safe ===
                                    CommonQuestionValueTypes.NotSure
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "safe",
                            CommonQuestionValueTypes.NotSure
                          );
                          setOpenGeneralInterventionPopup(true);
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Not Sure
                        </p>
                      </button>
                    </div>
                    <div className="py-2 px-6 flex flex-row space-x-5 h-[68px] bg-gray-20 items-center">
                      <p className="min-w-[320px] flex flex-col col-span-4 pr-8">
                        <span className="flex flex-row font-semibold text-lg text-teal-300">
                          Assess for Proper Technique{" "}
                        </span>
                        <span className="flex text-[13px] text-gray-800 font-medium">
                          (If Applicable)
                        </span>
                      </p>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[188px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.technique ===
                                    CommonQuestionValueTypes.Yes
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "technique",
                            CommonQuestionValueTypes.Yes
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Yes
                        </p>
                      </button>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[191px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.technique ===
                                    CommonQuestionValueTypes.No
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "technique",
                            CommonQuestionValueTypes.No
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          No
                        </p>
                      </button>
                      <button
                        className={`font-semibold text-body-md p-3 h-[40px] justify-center items-center flex rounded-[20px] border-[1px] min-w-[188px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100
                                  ${
                                    medicationCardObj.technique ===
                                    CommonQuestionValueTypes.NotSure
                                      ? "bg-blue-100"
                                      : "bg-white"
                                  }`}
                        onClick={() => {
                          handleSelection(
                            medicationCardObj.id,
                            "technique",
                            CommonQuestionValueTypes.NotSure
                          );
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Not Applicable
                        </p>
                      </button>
                    </div>
                    <div className="py-2 px-4 flex flex-col gap-2 items-stretch min-h-[50px] mt-2">
                      <div className="w-full rounded-[4px] border border-gray-200 bg-white px-2 py-1">
                      <label
                        htmlFor={`pharmacist-assessment-comments-${medicationCardObj.id}`}
                        className="block font-normal text-gray-500 text-body-sm"
                      >
                        Pharmacist Assessment Comments{" "}
                        <span className="font-normal text-gray-500">
                          (Optional)
                        </span>
                      </label>
                        <textarea
                          ref={assessmentCommentTextareaRef}
                          id={`pharmacist-assessment-comments-${medicationCardObj.id}`}
                          rows={1}
                          className="w-full min-h-[50px] max-h-[7.5rem] resize-none overflow-y-auto border-none bg-transparent py-1 pl-0 pr-1 text-md text-gray-800 focus:outline-none focus:ring-0 focus:border-none disabled:opacity-60 disabled:bg-gray-50"
                          value={medicationCardObj.comment ?? ""}
                          onChange={(e) =>
                            handleSelection(
                              medicationCardObj.id,
                              "comment",
                              e.target.value
                            )
                          }
                          disabled={
                            !initialRevisitedCardsValue.some(
                              (x) => x.id === medicationCardObj.id
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="py-2 px-4 flex flex-col gap-2 items-stretch pb-4">
                      <CustomInput
                        type="multi-dropdown"
                        name={`special-instructions-${medicationCardObj.id}`}
                        label="Special Instructions"
                        placeholder="Special Instructions"
                        options={specialInstructionOptions}
                        className="w-full"
                        customMaxHeight="max-h-[240px]"
                        value={specialInstructionsParsed.presetValues}
                        onChange={(selected) => {
                          const next = Array.isArray(selected)
                            ? selected
                            : selected
                              ? [selected]
                              : [];
                          handleSelection(
                            medicationCardObj.id,
                            "specialIntructions",
                            buildSpecialInstructionsMulti(
                              next,
                              next.includes(SPECIAL_INSTRUCTION_OTHER_VALUE)
                                ? specialInstructionsParsed.customText
                                : ""
                            )
                          );
                        }}
                        disabled={siDisabled}
                        showOptionalLabel
                      />
                      {specialInstructionsParsed.presetValues.includes(
                        SPECIAL_INSTRUCTION_OTHER_VALUE
                      ) && (
                        <div className="w-full rounded-[4px] border border-gray-200 bg-white px-2 py-2 flex flex-col gap-1">
                          <label
                            htmlFor={`special-instructions-custom-${medicationCardObj.id}`}
                            className="block font-normal text-gray-900 text-xs leading-[18px]"
                          >
                            Add Custom Instructions{" "}
                            <span className="font-normal text-gray-600">
                              (Optional)
                            </span>
                          </label>
                          <textarea
                            id={`special-instructions-custom-${medicationCardObj.id}`}
                            rows={2}
                            placeholder="Enter custom instructions"
                            className="w-full min-h-[50px] max-h-[7.5rem] resize-y overflow-y-auto border-none bg-transparent py-1 pl-0 pr-1 text-md text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-none disabled:opacity-60 disabled:bg-gray-50"
                            value={specialInstructionsParsed.customText}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              handleSelection(
                                medicationCardObj.id,
                                "specialIntructions",
                                buildSpecialInstructionsMulti(
                                  specialInstructionsParsed.presetValues,
                                  e.target.value
                                )
                              )
                            }
                            disabled={
                              !initialRevisitedCardsValue.some(
                                (x) => x.id === medicationCardObj.id
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 flex-row items-center justify-end space-x-5 border-t border-gray-100 bg-gray-20 py-4 px-5">
                    {medicationCardObj.status !== "skipped" ? (
                      <button
                        className={`font-semibold text-body-md p-3 rounded-[4px] border-[1px] min-h-[62px] min-w-[200px] bg-white border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-gray-50
                                        
                            ${
                              !updatedCards.includes(medicationCardObj.id) &&
                              medicationCardObj.status !== "blank" &&
                              "opacity-60 pointer-events-none"
                            }`}
                        onClick={() => {
                          onSkipReview(medicationCardObj);
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          Skip
                        </p>
                      </button>
                    ) : (
                      <div className="flex flex-row justify-center min-w-[200px] min-h-[62px] px-2">
                        <span className="text-gray-600 font-bold text-base items-center flex">
                          Skipped
                        </span>
                        <ReactSVG
                          src="/icons/medication-card-skip.svg"
                          className={`pl-2 w-fit flex items-center justify-center`}
                        />
                      </div>
                    )}
                    {initialRevisitedCardsValue.some(
                      (x) => x.id === medicationCardObj.id
                    ) || medicationCardObj.status !== "reviewed" ? (
                      <button
                        className={`font-semibold text-body-md p-3 rounded-[4px] border-[1px] min-h-[62px] min-w-[200px] bg-REELTeal border-transparent hover:bg-teal-200 text-white disabled:opacity-50           
                            ${
                              !initialRevisitedCardsValue.some(
                                (x) => x.id === medicationCardObj.id
                              ) &&
                              medicationCardObj.status !== "blank" &&
                              "opacity-60 pointer-events-none"
                            }`}
                        disabled={
                          medicationCardObj.status !== "skipped" &&
                          (medicationCardObj.consumption === null ||
                            medicationCardObj.working === null ||
                            medicationCardObj.safe === null ||
                            medicationCardObj.reasonForMedication === null ||
                            medicationCardObj.medicationType === null ||
                            (complianceAction !== "Edit" && showCompliance))
                        }
                        onClick={() => {
                          if (conditionNameInputMode) {
                            setOpenUncodedDrug(true);
                          } else {
                            onSaveAndContinue(medicationCardObj);
                          }
                        }}
                      >
                        <p className="font-semibold text-body-md flex justify-center items-center">
                          {initialRevisitedCardsValue.some(
                            (x) => x?.id === medicationCardObj.id
                          ) &&
                          updatedCards.includes(medicationCardObj.id) &&
                          medicationCardObj.status !== "blank"
                            ? "Update & Save"
                            : "Save & Continue"}
                        </p>
                      </button>
                    ) : (
                      <>
                        {medicationCardObj.intervention &&
                        medicationCardObj.intervention.urgencyLevel !== null ? (
                          <div className="flex flex-row justify-center min-w-[200px] min-h-[62px] pl-16 pr-2">
                            <span className="w-fit text-REELTeal font-semibold text-base items-center flex">
                              Intervention
                            </span>
                            <ReactSVG
                              src="/icons/medication-card-intervention.svg"
                              onClick={() => {
                                setshowCreateDialog(true);
                              }}
                              className={`pl-4 w-fit flex items-center justify-center`}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-row justify-center min-w-[200px] min-h-[62px] pl-16 pr-2">
                            <span className="w-fit text-REELTeal font-bold text-base items-center flex">
                              Complete
                            </span>
                            <ReactSVG
                              src="/icons/medication-card-complete.svg"
                              className={`pl-4 w-fit flex items-center justify-center`}
                            />
                          </div>
                        )}
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>
          {/* compliance package details */}
          {showCompliance && (
            <div
              className={`w-[360px] ${
                (medicationCardObj.name === null ||
                  medicationCardObj.name === undefined ||
                  !onFocus) &&
                "pointer-events-none opacity-60"
              }`}
            >
              {/* header */}
              <div className="bg-gray-20 h-[197px] gap-5">
                <div className="flex flex-row justify-between px-4 py-8">
                  <h3 className="text-xl font-semibold text-teal-300">
                    Compliance Package Details
                  </h3>
                </div>
                <div className={`flex flex-row space-x-5`}>
                  <div className="flex flex-col w-full">
                    {complianceAction === "Edit" ? (
                      <Alert
                        title="Administration Times Saved"
                        type="success"
                        className="mb-4 !text-base border-l-1 !h-[80px] border-r-1 rounded-none"
                        // handleClose={() => setInactivePatientAlert(false)}
                        hideCross={true}
                      />
                    ) : (
                      <Alert
                        title="Complete Administration Times"
                        type="warning"
                        className="mb-4 !text-base border-l-1 !h-[80px] border-r-1 rounded-none"
                        // handleClose={() => setInactivePatientAlert(false)}
                        hideCross={true}
                      />
                    )}
                  </div>
                </div>
              </div>
              {/* body */}
              <div
                className={`flex flex-col text-gray-800 gap-3 pt-3 pb-6 px-5 ${
                  !initialRevisitedCardsValue.some(
                    (x) => x.id === medicationCardObj.id
                  ) && "pointer-events-none"
                }`}
              >
                {/* Apply justify-between to distribute space between part-1 and part-2 */}
                <div className="flex flex-col justify-between min-h-[355px]">
                  {/* part 1 */}
                  <div
                    className={`flex flex-col gap-5 ${
                      complianceAction === "Edit" && "pointer-events-none"
                    }`}
                  >
                    <div
                      className={
                        "text-gray-800 border-[1px] justify-center items-center flex h-[48px] border-gray-100 rounded-[4px]"
                      }
                    >
                      <Button
                        className={`!border-[0px] !h-[40px] !w-[153px] !px-2 !text-body-sm`}
                        type={isGeneralTime ? "primary" : "primary-outline"}
                        text={"General Time"}
                        onClick={() => {
                          setIsGeneralTime(true);
                        }}
                      />
                      <Button
                        className={`!border-[0px] !h-[40px] !w-[153px] !px-2 !text-body-md`}
                        type={!isGeneralTime ? "primary" : "primary-outline"}
                        text={"Specific Time"}
                        onClick={() => {
                          setIsGeneralTime(false);
                        }}
                      />
                    </div>

                    {isGeneralTime && medicationCardObj.generalTime && (
                      <div className="flex flex-row gap-[10px] justify-between">
                        {Object.entries(GeneralTimeTypes).map(
                          ([key, value]) => (
                            <button
                              className={`font-semibold text-body-md p-3 h-[40px] min-w-[71px] justify-center items-center flex rounded-[10px] border-[1px] border-gray-100
              ${
                medicationCardObj.generalTime?.includes(value)
                  ? "text-white bg-REELTeal"
                  : "bg-white text-gray-700"
              }`}
                              onClick={() => {
                                if (
                                  medicationCardObj.generalTime.includes(value)
                                ) {
                                  handleSelection(
                                    medicationCardObj.id,
                                    "generalTime",
                                    medicationCardObj.generalTime.filter(
                                      (x) => x !== value
                                    )
                                  );
                                } else {
                                  handleSelection(
                                    medicationCardObj.id,
                                    "generalTime",
                                    [...medicationCardObj.generalTime, value]
                                  );
                                }
                              }}
                            >
                              <p className="font-semibold text-base flex justify-center items-center">
                                {value}
                              </p>
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>

                  {/* part 2 */}
                  <div className="flex">
                    <Button
                      className={`!w-full min-h-[62px]`}
                      type={
                        complianceAction !== "Edit"
                          ? "primary"
                          : "primary-outline"
                      }
                      text={complianceAction}
                      disabled={
                        !initialRevisitedCardsValue.some(
                          (x) => x.id === medicationCardObj.id
                        )
                      }
                      onClick={() => {
                        setComplianceAction(
                          complianceAction !== "Edit" ? "Edit" : "Save"
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
      <InterventionAssessment
        create={true}
        patientId={patientId}
        open={showAssessmentDialog}
        step={1}
        maxSteps={1}
        onClose={(value) => {
          setshowAssessmentDialog(false);
        }}
        onIntervention={(assessMed) => {
          onSaveAssessment(assessMed, true);
          setshowAssessmentDialog(false);
          setshowCreateDialog(true);
        }}
        onSave={(assessMed) => {
          onSaveAssessment(assessMed, false);
          setshowAssessmentDialog(false);
        }}
        selectedMedicationCard={medicationCardObj}
        selectedDrug={medications.find((x) => x.id === medicationCardObj?.id)}
      />
      <InterventionCreate
        open={showCreateDialog}
        selectedMedication={medicationCardObj}
        onClose={(value) => {
          setshowCreateDialog(false);
        }}
        onSave={(intMed, interventionId) => {
          onSaveInterventionAndAssessment(intMed, interventionId, false);
          setshowCreateDialog(false);
        }}
        goBack={(intMed, interventionId) => {
          onSaveInterventionAndAssessment(intMed, interventionId, true);
          setshowCreateDialog(false);
          setshowAssessmentDialog(true);
        }}
      />
      <ConfirmationModalNew
        open={openUncodedDrug}
        title={"Uncoded Drug Alert"}
        type={"primary"}
        confirmText={"Continue"}
        cancelText="Cancel & Review"
        content={() => (
          <p>
            An uncoded Medical Condition has been added. Profile Analysis cannot
            be performed. A manual clinical check will be required.
          </p>
        )}
        onCross={() => {
          setOpenUncodedDrug(false);
        }}
        handleClose={() => {
          setOpenUncodedDrug(false);
        }}
        confirmAction={() => {
          setConditionNameInputMode(false);
          setOpenUncodedDrug(false);
          onSaveAndContinue(medicationCardObj);
        }}
      />
      <ConfirmationModalNew
        open={showPrescribedDialog}
        title={prescribedDialogTitle}
        type={"primary"}
        confirmText={"Continue with Review"}
        cancelText={null}
        cancelBtnType="primary-outline"
        className="!w-[630px]"
        content={() => (
          <p className="text-gray-800">{prescribedDialogMessage}</p>
        )}
        onCross={() => {
          setShowPrescribedDialog(false);
        }}
        confirmAction={() => {
          setShowPrescribedDialog(false);
          onContinue(medicationCardObj);
        }}
        extraButtonComponent={() => (
          <Button
            className="min-w-fit !px-3"
            type="primary-outline"
            text="Create Intervention"
            onClick={() => {
              setShowPrescribedDialog(false);
              setshowAssessmentDialog(true);
            }}
          />
        )}
      />
      <CustomTimeDialog
        open={openCustomTimePopup}
        buttonRef={customizeButtonRef}
        onCross={() => {
          setOpenCustomTimePopup(false);
        }}
        handleClose={() => {
          setOpenCustomTimePopup(false);
        }}
        confirmAction={(time) => {
          setOpenCustomTimePopup(false);
          handleSelection(medicationCardObj.id, "generalTime", time);
        }}
        time={medicationCardObj?.generalTime}
      />
    </div>
  );
};

export default MedicationReviewCard;
